import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from '@mui/material';
import { Drug } from '../../../application/types/drug';

interface EditDrugIdentifiersDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: {
        atc?: string;
        barCode?: string;
        gtin?: string;
    }) => Promise<void>;
    drug: Drug | null;
}

const EditDrugIdentifiersDialog: React.FC<EditDrugIdentifiersDialogProps> = ({
    open,
    onClose,
    onSave,
    drug,
}) => {
    const [atc, setAtc] = useState('');
    const [barCode, setBarCode] = useState('');
    const [gtin, setGtin] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && drug) {
            setAtc(drug.atc || '');
            setBarCode(drug.barCode || '');
            setGtin(drug.gtin || '');
            setError(null);
        }
    }, [open, drug]);

    const handleSave = async () => {
        setError(null);
        setSaving(true);

        try {
            await onSave({
                atc: atc.trim() || undefined,
                barCode: barCode.trim() || undefined,
                gtin: gtin.trim() || undefined,
            });
            onClose();
        } catch (err: any) {
            const responseData = err.response?.data;
            let errorMessage = 'Ett fel uppstod';

            if (typeof responseData === 'string') {
                errorMessage = responseData;
            } else if (responseData?.exception) {
                errorMessage = responseData.exception;
            } else if (responseData?.messages?.length > 0) {
                errorMessage = responseData.messages.join(', ');
            } else if (responseData?.message) {
                errorMessage = responseData.message;
            } else if (err.message) {
                errorMessage = err.message;
            }

            setError(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Redigera läkemedelsidentifierare</DialogTitle>
            <DialogContent>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 1,
                    }}
                >
                    {error && (
                        <Alert severity="error" onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    <TextField
                        label="ATC-kod"
                        value={atc}
                        onChange={(e) => setAtc(e.target.value)}
                        fullWidth
                        helperText="Anatomisk Terapeutisk Kemisk klassifikation"
                    />

                    <TextField
                        label="Streckkod"
                        value={barCode}
                        onChange={(e) => setBarCode(e.target.value)}
                        fullWidth
                        helperText="Läkemedlets streckkod"
                    />

                    <TextField
                        label="GTIN"
                        value={gtin}
                        onChange={(e) => setGtin(e.target.value)}
                        fullWidth
                        helperText="GTIN (PC)"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Avbryt
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving}
                >
                    {saving ? 'Sparar...' : 'Spara'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditDrugIdentifiersDialog;
